import React, { useEffect, useState } from 'react';
import { Row, Col, Card, CardBody, Badge, Spinner } from 'reactstrap';
import Dropzone from 'react-dropzone';
import { get, put } from 'helpers/api_helper';
import { showSuccessAlert, showErroeAlert } from 'pages/utils/alertMessages';
import { CLINIC_URL } from 'helpers/url_helper';
import cookieHelper from 'helpers/getCookieData';
import { useTranslation } from 'react-i18next';
import CountrySelector from 'components/Common/CountrySelector';
import CurrencySelector from 'components/Common/CurrencySelector';
import moment from 'moment';
import {
  RiHospitalLine,
  RiMapPinLine,
  RiPhoneLine,
  RiMailLine,
  RiTimeLine,
  RiGlobalLine,
  RiTabletLine,
  RiLockPasswordLine,
  RiFileCopyLine,
  RiCheckLine,
  RiExternalLinkLine,
  RiUploadCloudLine,
  RiSaveLine,
  RiStethoscopeLine,
  RiShieldCheckLine,
  RiBuilding4Line,
  RiInformationLine,
  RiQrCodeLine
} from 'react-icons/ri';

const ManageClinic = () => {
  const { t } = useTranslation();
  const [clinicData, setClinicData] = useState(null);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [copiedField, setCopiedField] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);

  const cookieClinic = cookieHelper.getCookie('_c')
    ? JSON.parse(atob(cookieHelper.getCookie('_c')))
    : null;

  const fetchClinicData = async () => {
    try {
      setLoading(true);
      const res = await get(`clinics/${cookieClinic?.id}`);
      if (res.success && res.body) {
        setClinicData(res.body);
        setFormData({
          clinic_name: res.body.clinic_name || '',
          doctor_name: res.body.doctors?.name || res.body.doctor_name || '',
          doctor_code: res.body.doctor_code || '',
          address: res.body.address || '',
          city: res.body.city || '',
          state: res.body.state || '',
          country: res.body.country || 'India',
          country_code: res.body.country_code || 'IN',
          zip_code: res.body.zip_code || '',
          phone: res.body.phone || '',
          email: res.body.email || '',
          time_zone: res.body.time_zone || 'Asia/Kolkata',
          currency_code: res.body.currency_code || 'INR',
          slug: res.body.slug || '',
        });
        if (res.body.profile_pic) {
          setLogoPreview(res.body.profile_pic);
        }
      }
    } catch (err) {
      console.error('Error fetching clinic data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClinicData();
  }, []);

  const handleFieldChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAcceptedFiles = (files) => {
    if (files && files.length > 0) {
      const file = files[0];
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
        setFormData((prev) => ({
          ...prev,
          profile_pic: reader.result,
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpdate = async (e) => {
    if (e) e.preventDefault();
    try {
      setSaving(true);
      const res = await put(`${CLINIC_URL}/${cookieClinic?.id}`, formData);
      if (res.success) {
        showSuccessAlert(t('Clinic profile updated successfully'));
        fetchClinicData();
      } else {
        showErroeAlert(res.message || t('Failed to update clinic'));
      }
    } catch (err) {
      showErroeAlert(err.message || t('An error occurred during update'));
    } finally {
      setSaving(false);
    }
  };

  const handleCopy = (text, fieldName) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedField(fieldName);
    setTimeout(() => {
      setCopiedField(null);
    }, 2000);
  };

  if (loading && !clinicData) {
    return (
      <div className="text-center py-5">
        <Spinner color="warning" />
        <div className="text-muted mt-2 font-size-13">{t('Loading clinic details...')}</div>
      </div>
    );
  }

  const kioskCode = clinicData?.kiosk_code && clinicData.kiosk_code !== '0' ? clinicData.kiosk_code : `KSK-${clinicData?.id || '88'}`;
  const accessCode = clinicData?.access_code && clinicData.access_code !== '0' ? clinicData.access_code : `OPD-ACC-${clinicData?.id || '909'}`;
  const publicLink = clinicData?.clinic_url && clinicData.clinic_url !== '0' 
    ? clinicData.clinic_url 
    : `https://app.oralstop.com/book/${clinicData?.slug || clinicData?.id || ''}`;

  return (
    <div className="manage-clinic-redesign">
      {/* Top Clinic Hero Banner */}
      <div className="clinic-hero-banner mb-3 p-3 p-md-4 rounded-3 d-flex align-items-center justify-content-between flex-wrap gap-3">
        <div className="d-flex align-items-center gap-3">
          <div
            className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 font-size-22 font-weight-bold shadow-sm"
            style={{
              width: '56px',
              height: '56px',
              backgroundColor: '#F8CA15',
              color: '#0f172a',
              border: '2px solid rgba(255, 255, 255, 0.3)',
            }}
          >
            {logoPreview ? (
              <img
                src={logoPreview}
                alt="Logo"
                className="rounded-circle"
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : (
              <RiHospitalLine />
            )}
          </div>
          <div>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <h4 className="mb-0 font-weight-bold clinic-title font-size-18">
                {formData.clinic_name || clinicData?.clinic_name || t('Main Dental Clinic')}
              </h4>
              <Badge color="success" className="d-inline-flex align-items-center gap-1 font-size-11 px-2 py-0.5">
                <RiShieldCheckLine /> {t('Active & Verified')}
              </Badge>
              {clinicData?.is_default ? (
                <Badge color="warning" className="text-dark font-size-11 px-2 py-0.5 font-weight-semibold">
                  {t('Primary Branch')}
                </Badge>
              ) : null}
            </div>
            <div className="d-flex align-items-center gap-3 mt-1 font-size-12 flex-wrap clinic-hero-meta">
              <span className="d-inline-flex align-items-center gap-1">
                <RiBuilding4Line className="clinic-meta-icon text-warning" />
                <span>Branch ID:</span> <strong className="text-white">#CLI-{clinicData?.id || '1'}</strong>
              </span>
              <span className="d-inline-flex align-items-center gap-1">
                <RiTimeLine className="clinic-meta-icon text-warning" />
                <span>{formData.time_zone || clinicData?.time_zone || 'Asia/Kolkata'}</span>
              </span>
              <span className="d-inline-flex align-items-center gap-1">
                <RiGlobalLine className="clinic-meta-icon text-warning" />
                <span>{formData.currency_code || 'INR'} ({formData.country || 'India'})</span>
              </span>
            </div>
          </div>
        </div>

        {/* Quick Action Top Button */}
        <div className="d-flex align-items-center gap-2">
          <button
            type="button"
            className="btn btn-warning font-weight-bold d-inline-flex align-items-center gap-1.5 shadow-sm px-3 py-2"
            onClick={handleUpdate}
            disabled={saving}
            style={{ backgroundColor: '#F8CA15', borderColor: '#EAB308', color: '#0f172a' }}
          >
            {saving ? (
              <>
                <Spinner size="sm" />
                <span>{t('Saving...')}</span>
              </>
            ) : (
              <>
                <RiSaveLine className="font-size-16" />
                <span>{t('Save Changes')}</span>
              </>
            )}
          </button>
        </div>
      </div>

      <Row className="g-3">
        {/* Left Column: Form Details */}
        <Col lg={8}>
          {/* 1. Identity & Branding */}
          <Card className="border shadow-2xs mb-3" style={{ borderRadius: '10px' }}>
            <div className="card-header bg-white border-bottom py-2.5 px-3 d-flex align-items-center gap-2">
              <div className="p-1.5 rounded bg-warning-subtle text-warning font-size-15 d-flex align-items-center justify-content-center">
                <RiHospitalLine />
              </div>
              <h6 className="mb-0 font-weight-bold text-slate-800 font-size-14">
                {t('Clinic Brand & Doctor Incharge')}
              </h6>
            </div>
            <CardBody className="p-3">
              <Row className="g-3 align-items-center">
                {/* Logo Upload Zone */}
                <Col md={3} className="text-center">
                  <Dropzone onDrop={handleAcceptedFiles} accept="image/*" multiple={false}>
                    {({ getRootProps, getInputProps, isDragActive }) => (
                      <div
                        {...getRootProps()}
                        className={`p-2 border border-2 border-dashed rounded-3 text-center cursor-pointer transition-all ${
                          isDragActive ? 'border-primary bg-primary-subtle' : 'border-slate-200 bg-light'
                        }`}
                        style={{ minHeight: '110px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}
                      >
                        <input {...getInputProps()} />
                        {logoPreview ? (
                          <div className="position-relative">
                            <img
                              src={logoPreview}
                              alt="Clinic Logo Preview"
                              className="rounded-2 shadow-2xs"
                              style={{ maxHeight: '70px', maxWidth: '100%', objectFit: 'contain' }}
                            />
                            <div className="text-primary font-size-10 font-weight-bold mt-1">
                              {t('Click to change')}
                            </div>
                          </div>
                        ) : (
                          <>
                            <RiUploadCloudLine className="font-size-26 text-muted mb-1" />
                            <span className="font-size-11 font-weight-semibold text-slate-700">
                              {t('Upload Logo')}
                            </span>
                            <span className="text-muted font-size-10">{t('PNG, JPG up to 2MB')}</span>
                          </>
                        )}
                      </div>
                    )}
                  </Dropzone>
                </Col>

                {/* Main Fields */}
                <Col md={9}>
                  <div className="mb-2.5">
                    <label className="form-label font-size-12 font-weight-semibold text-slate-700 mb-1">
                      {t('Clinic / Hospital Full Name *')}
                    </label>
                    <div className="input-group input-group-sm">
                      <span className="input-group-text bg-light border-slate-200 text-muted">
                        <RiHospitalLine />
                      </span>
                      <input
                        type="text"
                        className="form-control font-size-13 font-weight-semibold"
                        placeholder={t('e.g. Praveen Dental Hospital & Implant Centre')}
                        value={formData.clinic_name}
                        onChange={(e) => handleFieldChange('clinic_name', e.target.value)}
                      />
                    </div>
                  </div>

                  <Row className="g-2">
                    <Col md={7}>
                      <div>
                        <label className="form-label font-size-12 font-weight-semibold text-slate-700 mb-1">
                          {t('Doctor / Medical Director Name')}
                        </label>
                        <div className="input-group input-group-sm">
                          <span className="input-group-text bg-light border-slate-200 text-muted">
                            <RiStethoscopeLine />
                          </span>
                          <input
                            type="text"
                            className="form-control font-size-13"
                            placeholder={t('e.g. Dr. Praveen Yennam, MDS')}
                            value={formData.doctor_name}
                            onChange={(e) => handleFieldChange('doctor_name', e.target.value)}
                          />
                        </div>
                      </div>
                    </Col>
                    <Col md={5}>
                      <div>
                        <label className="form-label font-size-12 font-weight-semibold text-slate-700 mb-1">
                          {t('Branch Slug / URL')}
                        </label>
                        <input
                          type="text"
                          className="form-control form-control-sm font-monospace font-size-12"
                          placeholder="e.g. main-branch"
                          value={formData.slug}
                          onChange={(e) => handleFieldChange('slug', e.target.value)}
                        />
                      </div>
                    </Col>
                  </Row>
                </Col>
              </Row>
            </CardBody>
          </Card>

          {/* 2. Clinic Address & Location */}
          <Card className="border shadow-2xs mb-3" style={{ borderRadius: '10px' }}>
            <div className="card-header bg-white border-bottom py-2.5 px-3 d-flex align-items-center gap-2">
              <div className="p-1.5 rounded bg-primary-subtle text-primary font-size-15 d-flex align-items-center justify-content-center">
                <RiMapPinLine />
              </div>
              <h6 className="mb-0 font-weight-bold text-slate-800 font-size-14">
                {t('Location & Address Details')}
              </h6>
            </div>
            <CardBody className="p-3">
              <div className="mb-2.5">
                <label className="form-label font-size-12 font-weight-semibold text-slate-700 mb-1">
                  {t('Premises & Street Address')}
                </label>
                <div className="input-group input-group-sm">
                  <span className="input-group-text bg-light border-slate-200 text-muted">
                    <RiMapPinLine />
                  </span>
                  <input
                    type="text"
                    className="form-control font-size-13"
                    placeholder={t('Building No, Floor, Landmark, Street Name...')}
                    value={formData.address}
                    onChange={(e) => handleFieldChange('address', e.target.value)}
                  />
                </div>
              </div>

              <Row className="g-2">
                <Col md={3} sm={6}>
                  <div>
                    <label className="form-label font-size-12 font-weight-semibold text-slate-700 mb-1">
                      {t('City')}
                    </label>
                    <input
                      type="text"
                      className="form-control form-control-sm font-size-13"
                      placeholder={t('City')}
                      value={formData.city}
                      onChange={(e) => handleFieldChange('city', e.target.value)}
                    />
                  </div>
                </Col>
                <Col md={3} sm={6}>
                  <div>
                    <label className="form-label font-size-12 font-weight-semibold text-slate-700 mb-1">
                      {t('State / Province')}
                    </label>
                    <input
                      type="text"
                      className="form-control form-control-sm font-size-13"
                      placeholder={t('State')}
                      value={formData.state}
                      onChange={(e) => handleFieldChange('state', e.target.value)}
                    />
                  </div>
                </Col>
                <Col md={3} sm={6}>
                  <div>
                    <label className="form-label font-size-12 font-weight-semibold text-slate-700 mb-1">
                      {t('Country')}
                    </label>
                    <CountrySelector
                      value={formData.country_code || 'IN'}
                      onCountrySelect={(defaults) => {
                        setFormData((prev) => ({
                          ...prev,
                          country: defaults.name,
                          country_code: defaults.code,
                          time_zone: defaults.default_timezone,
                          currency_code: defaults.default_currency,
                        }));
                      }}
                    />
                  </div>
                </Col>
                <Col md={3} sm={6}>
                  <div>
                    <label className="form-label font-size-12 font-weight-semibold text-slate-700 mb-1">
                      {t('Zip / Postal Code')}
                    </label>
                    <input
                      type="text"
                      className="form-control form-control-sm font-size-13 font-monospace"
                      placeholder={t('Postal Code')}
                      value={formData.zip_code}
                      onChange={(e) => handleFieldChange('zip_code', e.target.value)}
                    />
                  </div>
                </Col>
              </Row>
            </CardBody>
          </Card>

          {/* 3. Contact & Regional Parameters */}
          <Card className="border shadow-2xs mb-3" style={{ borderRadius: '10px' }}>
            <div className="card-header bg-white border-bottom py-2.5 px-3 d-flex align-items-center gap-2">
              <div className="p-1.5 rounded bg-info-subtle text-info font-size-15 d-flex align-items-center justify-content-center">
                <RiPhoneLine />
              </div>
              <h6 className="mb-0 font-weight-bold text-slate-800 font-size-14">
                {t('Contact Info & Financial Currency')}
              </h6>
            </div>
            <CardBody className="p-3">
              <Row className="g-3">
                <Col md={6}>
                  <div>
                    <label className="form-label font-size-12 font-weight-semibold text-slate-700 mb-1">
                      {t('Primary Contact / Mobile No')}
                    </label>
                    <div className="input-group input-group-sm">
                      <span className="input-group-text bg-light border-slate-200 text-muted">
                        <RiPhoneLine />
                      </span>
                      <input
                        type="text"
                        className="form-control font-size-13"
                        placeholder={t('e.g. +91 9876543210')}
                        value={formData.phone}
                        onChange={(e) => handleFieldChange('phone', e.target.value)}
                      />
                    </div>
                  </div>
                </Col>
                <Col md={6}>
                  <div>
                    <label className="form-label font-size-12 font-weight-semibold text-slate-700 mb-1">
                      {t('Clinic Official Email')}
                    </label>
                    <div className="input-group input-group-sm">
                      <span className="input-group-text bg-light border-slate-200 text-muted">
                        <RiMailLine />
                      </span>
                      <input
                        type="email"
                        className="form-control font-size-13"
                        placeholder={t('e.g. clinic@example.com')}
                        value={formData.email}
                        onChange={(e) => handleFieldChange('email', e.target.value)}
                      />
                    </div>
                  </div>
                </Col>
                <Col md={6}>
                  <div>
                    <label className="form-label font-size-12 font-weight-semibold text-slate-700 mb-1">
                      {t('Operating Timezone')}
                    </label>
                    <div className="input-group input-group-sm">
                      <span className="input-group-text bg-light border-slate-200 text-muted">
                        <RiTimeLine />
                      </span>
                      <input
                        type="text"
                        className="form-control font-size-13 font-monospace"
                        placeholder="e.g. Asia/Kolkata"
                        value={formData.time_zone}
                        onChange={(e) => handleFieldChange('time_zone', e.target.value)}
                      />
                    </div>
                  </div>
                </Col>
                <Col md={6}>
                  <div>
                    <label className="form-label font-size-12 font-weight-semibold text-slate-700 mb-1">
                      {t('Billing & Operating Currency')}
                    </label>
                    <CurrencySelector
                      value={formData.currency_code || 'INR'}
                      onChange={(e) => handleFieldChange('currency_code', e.target.value)}
                    />
                  </div>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>

        {/* Right Column: Digital Portal & Kiosk Keys */}
        <Col lg={4}>
          {/* Card 1: Kiosk Access Portal */}
          <Card className="border shadow-2xs mb-3 overflow-hidden" style={{ borderRadius: '10px' }}>
            <div className="card-header bg-light border-bottom py-2.5 px-3 d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center gap-2">
                <RiTabletLine className="text-primary font-size-16" />
                <span className="font-weight-bold font-size-13 text-slate-800">
                  {t('Reception Kiosk Code')}
                </span>
              </div>
              <Badge color="primary-subtle" className="text-primary font-size-10">
                {t('Tablet Check-in')}
              </Badge>
            </div>
            <CardBody className="p-3">
              <p className="text-muted font-size-11 mb-2">
                {t('Enter this access key on your clinic reception tablet for self-service patient registration.')}
              </p>
              <div className="d-flex align-items-center justify-content-between p-2 rounded-2 bg-light border border-slate-200">
                <code className="font-weight-bold font-size-15 text-slate-900 tracking-wider">
                  {kioskCode}
                </code>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary py-1 px-2 d-inline-flex align-items-center gap-1 font-size-11"
                  onClick={() => handleCopy(kioskCode, 'kiosk')}
                >
                  {copiedField === 'kiosk' ? <RiCheckLine className="text-success" /> : <RiFileCopyLine />}
                  <span>{copiedField === 'kiosk' ? t('Copied') : t('Copy')}</span>
                </button>
              </div>
            </CardBody>
          </Card>

          {/* Card 2: Clinic Access Security Key */}
          <Card className="border shadow-2xs mb-3 overflow-hidden" style={{ borderRadius: '10px' }}>
            <div className="card-header bg-light border-bottom py-2.5 px-3 d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center gap-2">
                <RiLockPasswordLine className="text-warning font-size-16" />
                <span className="font-weight-bold font-size-13 text-slate-800">
                  {t('Clinic Security Key')}
                </span>
              </div>
              <Badge color="warning-subtle" className="text-warning font-size-10">
                {t('Internal')}
              </Badge>
            </div>
            <CardBody className="p-3">
              <p className="text-muted font-size-11 mb-2">
                {t('Authentication passcode for doctor and staff mobile integrations.')}
              </p>
              <div className="d-flex align-items-center justify-content-between p-2 rounded-2 bg-light border border-slate-200">
                <code className="font-weight-bold font-size-14 text-slate-900">
                  {accessCode}
                </code>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary py-1 px-2 d-inline-flex align-items-center gap-1 font-size-11"
                  onClick={() => handleCopy(accessCode, 'access')}
                >
                  {copiedField === 'access' ? <RiCheckLine className="text-success" /> : <RiFileCopyLine />}
                  <span>{copiedField === 'access' ? t('Copied') : t('Copy')}</span>
                </button>
              </div>
            </CardBody>
          </Card>

          {/* Card 3: Online Booking Public URL */}
          <Card className="border shadow-2xs mb-3 overflow-hidden" style={{ borderRadius: '10px' }}>
            <div className="card-header bg-light border-bottom py-2.5 px-3 d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center gap-2">
                <RiGlobalLine className="text-success font-size-16" />
                <span className="font-weight-bold font-size-13 text-slate-800">
                  {t('Public Appointment Link')}
                </span>
              </div>
              <Badge color="success-subtle" className="text-success font-size-10">
                {t('Patient Web')}
              </Badge>
            </div>
            <CardBody className="p-3">
              <p className="text-muted font-size-11 mb-2">
                {t('Share this link on WhatsApp, Google Maps, or Website for direct online appointment bookings.')}
              </p>
              <div className="p-2 rounded-2 bg-light border border-slate-200 mb-2">
                <div className="text-truncate font-monospace font-size-11 text-slate-700" title={publicLink}>
                  {publicLink}
                </div>
              </div>
              <div className="d-flex align-items-center gap-2">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary w-50 d-inline-flex align-items-center justify-content-center gap-1 font-size-11 py-1"
                  onClick={() => handleCopy(publicLink, 'link')}
                >
                  {copiedField === 'link' ? <RiCheckLine className="text-success" /> : <RiFileCopyLine />}
                  <span>{copiedField === 'link' ? t('Copied Link') : t('Copy Link')}</span>
                </button>
                <a
                  href={publicLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-sm btn-outline-secondary w-50 d-inline-flex align-items-center justify-content-center gap-1 font-size-11 py-1"
                >
                  <RiExternalLinkLine />
                  <span>{t('Open')}</span>
                </a>
              </div>
            </CardBody>
          </Card>

          {/* Bottom Save Card */}
          <Card className="border shadow-2xs" style={{ borderRadius: '10px', backgroundColor: '#fcfcfd' }}>
            <CardBody className="p-3">
              <div className="d-grid gap-2">
                <button
                  type="button"
                  className="btn btn-warning font-weight-bold d-flex align-items-center justify-content-center gap-2 py-2 shadow-sm"
                  onClick={handleUpdate}
                  disabled={saving}
                  style={{ backgroundColor: '#F8CA15', borderColor: '#EAB308', color: '#0f172a' }}
                >
                  {saving ? (
                    <>
                      <Spinner size="sm" />
                      <span>{t('Updating Clinic...')}</span>
                    </>
                  ) : (
                    <>
                      <RiSaveLine className="font-size-16" />
                      <span>{t('Update Clinic Profile')}</span>
                    </>
                  )}
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-light border text-muted py-1.5"
                  onClick={fetchClinicData}
                  disabled={saving}
                >
                  {t('Discard Unsaved Changes')}
                </button>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default ManageClinic;
